import { useRoute } from 'nuxt/app'
import { resolveLocalAuthCookieUser, resolveRestoredAuthUser } from '../utils/auth-cookie'
import { resolveDefaultLoginRedirectUrl, resolveLoginRedirectUrl } from '../utils/login-redirect'
import { getCookieDomain } from '../utils/cookieDomain'

const YEAR_SECONDS = 60 * 60 * 24 * 365
const AUTH_COOKIE_NAMES = ['DJkdikKMG', 'ViLartidlakd', 'WsdsfgDGdagYms'] as const

type CookieOptions = {
  maxAge?: number
  domain?: string
  path?: string
  sameSite?: string
  secure?: boolean
}

const setCookie = (name: string, value: string, options: CookieOptions = {}) => {
  if (!import.meta.client) {
    return
  }

  let cookieString = `${name}=${encodeURIComponent(value)}`
  if (options.maxAge) cookieString += `; max-age=${options.maxAge}`
  if (options.domain) cookieString += `; domain=${options.domain}`
  if (options.path) cookieString += `; path=${options.path}`
  if (options.sameSite) cookieString += `; samesite=${options.sameSite}`
  if (options.secure) cookieString += '; secure'
  document.cookie = cookieString
}

const removeCookie = (name: string, domain?: string) => {
  if (!import.meta.client) {
    return
  }

  const parts = [`${name}=`, 'max-age=0', 'path=/']
  if (domain) parts.push(`domain=${domain}`)
  document.cookie = parts.join('; ')
}

const normalizeHost = (host = '') => host.replace(/^https?:\/\//, '').split('/')[0] || ''

const isLocalHost = (host = '') => {
  const hostname = normalizeHost(host).replace(/:\d+$/, '')

  return (
    !hostname ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname.startsWith('192.168.')
  )
}

const extractCookieValue = (response: any) =>
  response?.result?.Value ||
  response?.data?.Value ||
  response?.usercenter?.getcookie?.result?.Value ||
  response?.usercenter?.getcookie?.data?.Value ||
  response?.usercenter?.getcookie?.Value ||
  ''

const extractUserInfo = (response: any) =>
  response?.result ||
  response?.data ||
  response?.usercenter?.getuserfromcookie?.result ||
  response?.usercenter?.getuserfromcookie?.data ||
  null

const postToParent = (payload: Record<string, any>) => {
  if (!import.meta.client) {
    return
  }

  window.parent?.postMessage(payload, '*')
}

export const useLogin = () => {
  const appStore = useAppStore()
  const route = useRoute()
  const { getUserCookie, getUserFromCookie, invokeV3 } = useLoginApi()

  const getRuntimeHost = () =>
    normalizeHost(appStore.host || appStore.cookieHost || (import.meta.client ? window.location.host : ''))

  const resolveAuthCookieName = () => {
    if (appStore.isWiki === '1') return 'DJkdikKMG'
    if (appStore.isWiki === '2') return 'ViLartidlakd'
    return 'WsdsfgDGdagYms'
  }

  const readAuthCookieValue = () => {
    const preferredCookieName = resolveAuthCookieName()
    const cookieNames = [preferredCookieName, ...AUTH_COOKIE_NAMES.filter((name) => name !== preferredCookieName)]

    for (const cookieName of cookieNames) {
      const cookie = useCookie<string | null>(cookieName)
      if (typeof cookie.value === 'string' && cookie.value) {
        return cookie.value
      }
    }

    return ''
  }

  const getCookieSecurityOptions = (): CookieOptions => {
    if (!import.meta.client || window.location.protocol !== 'https:' || isLocalHost(getRuntimeHost())) {
      return {}
    }

    return {
      sameSite: 'none',
      secure: true,
    }
  }

  const removeCookieVariants = (name: string) => {
    const domain = getCookieDomain()
    removeCookie(name, domain)
    if (domain) {
      removeCookie(name)
    }
  }

  const fetchAuthCookieValue = async (userInfo: Record<string, any>) => {
    const directResponse = await getUserCookie(userInfo).catch(() => null)
    const directCookie = extractCookieValue(directResponse)
    if (directCookie) {
      return directCookie
    }

    const invokeResponse = await invokeV3('usercenter$getcookie', userInfo).catch(() => null)
    return extractCookieValue(invokeResponse)
  }

  const fetchUserInfoFromCookie = async (cookieValue: string) => {
    if (!cookieValue) {
      return null
    }

    const response = await getUserFromCookie({ contentcookie: cookieValue }).catch(() => null)
    return extractUserInfo(response)
  }

  const setLoginTokenCookies = (userInfo: any) => {
    const loginToken = userInfo?.LoginToken || userInfo
    const domain = getCookieDomain()

    if (!loginToken?.expires_in || !loginToken?.access_token) {
      return
    }

    const maxAge = Number(loginToken.expires_in)
    const bearerToken = `${loginToken.token_type || 'Bearer'} ${loginToken.access_token}`.trim()
    setCookie('logintoken', bearerToken, { maxAge, domain, path: '/' })
    setCookie('quitetoken', `${loginToken.access_token},${loginToken.refresh_token || ''}`, {
      maxAge,
      domain,
      path: '/',
    })
    setCookie('refreshtime', String(Date.now()), { maxAge, domain, path: '/' })
  }

  const writeAuthCookie = (cookieValue: string) => {
    const cookieName = resolveAuthCookieName()
    const domain = getCookieDomain()
    const securityOptions = getCookieSecurityOptions()

    AUTH_COOKIE_NAMES.forEach((name) => {
      if (name !== cookieName) {
        removeCookieVariants(name)
      }
    })

    setCookie(cookieName, cookieValue, {
      maxAge: YEAR_SECONDS,
      domain,
      path: '/',
      ...securityOptions,
    })
    appStore.setUsercookie(cookieValue)
  }

  const clearLoginCookies = () => {
    AUTH_COOKIE_NAMES.forEach((name) => removeCookieVariants(name))
    removeCookieVariants('userid')
    removeCookieVariants('logintoken')
    removeCookieVariants('quitetoken')
    removeCookieVariants('refreshtime')
    removeCookieVariants('userbaseinfo')
    removeCookieVariants('nickavatar')
    if (import.meta.client) {
      window.localStorage.removeItem('shared-app-user')
    }
    appStore.setUsercookie('')
    appStore.setUser({})
  }

  const afterLoginSuccess = (cookieValue: string) => {
    appStore.setLoginShow(false)
    postToParent({ op_type: 'reload', url: '', parm: cookieValue })

    if (import.meta.client && String(route.name || '').startsWith('userlogin') && !appStore.userlogin2) {
      const redirectUrl = resolveLoginRedirectUrl(
        route.query.returnurl,
        resolveDefaultLoginRedirectUrl(appStore.LanguageCode || 'en'),
        window.location.origin,
      )
      window.setTimeout(() => {
        window.location.href = redirectUrl
      }, 1200)
    }
  }

  const setCookieFromThirdParty = async (cookieValue: string, userInfo: Record<string, any> = {}) => {
    if (userInfo.userId || userInfo.nick || userInfo.avatar) {
      appStore.setUser(userInfo)
    }

    writeAuthCookie(cookieValue)
    const userFromCookie = await fetchUserInfoFromCookie(cookieValue)
    if (userFromCookie) {
      appStore.setUser({
        userId: userFromCookie.userId || '',
        nick: userFromCookie.nick || userFromCookie.nickName || '',
        avatar: userFromCookie.avatar || userFromCookie.profilePhoto || '',
      })
    }
    afterLoginSuccess(cookieValue)
  }

  const restoreLoginState = async () => {
    if (appStore.contentcookie && appStore.user?.userId) return

    const cookieValue = appStore.contentcookie || readAuthCookieValue()

    if (!cookieValue) return

    const storedUserId = useCookie<string | null>('userid').value || ''
    const localCookieUser = resolveLocalAuthCookieUser(cookieValue, storedUserId)
    if (localCookieUser) {
      appStore.setUsercookie(cookieValue)
      appStore.setUser(localCookieUser)
      return
    }

    const userInfo = await fetchUserInfoFromCookie(cookieValue)
    const cookieUser = resolveRestoredAuthUser(cookieValue, storedUserId, {
      userId: userInfo?.userId || '',
      nick: userInfo?.nick || userInfo?.nickName || '',
      avatar: userInfo?.avatar || userInfo?.profilePhoto || '',
    })
    appStore.setUsercookie(cookieValue)
    if (cookieUser) {
      appStore.setUser(cookieUser)
    }
  }

  const accessCookie = async (userInfo: any) => {
    const mergedUser = {
      userId: userInfo?.userId || '',
      nick: userInfo?.showNickName || userInfo?.nick || userInfo?.nickName || '',
      avatar: userInfo?.showAvataraddress || userInfo?.avatar || userInfo?.profilePhoto || '',
    }

    appStore.setUser(mergedUser)
    appStore.setLoginverify(false)
    setLoginTokenCookies(userInfo)

    const cookieValue = await fetchAuthCookieValue(mergedUser)

    if (cookieValue) {
      writeAuthCookie(cookieValue)
      afterLoginSuccess(cookieValue)
    }

    return cookieValue
  }

  return {
    accessCookie,
    clearLoginCookies,
    restoreLoginState,
    setLoginTokenCookies,
    setCookieFromThirdParty,
    writeAuthCookie,
  }
}
